import {
    CPM_LIVE_MASTER_STATUSES,
    formatCpmCount,
} from '../../../constants/centralProductMaster.constants';
import type {
    CpmAffectedProduct,
    CpmMasterDetailsResponse,
    CpmProductChoice,
} from '../../../types/centralProductMaster.types';
import ProductVolumeCollisions, {
    buildProductVolumeChoicesPayload,
    productCollisionsResolved,
} from './ProductVolumeCollisions';

const LIVE = new Set<string>(CPM_LIVE_MASTER_STATUSES);

export type BrandProductCollisionPair = {
    pairKey: string;
    displayName: string;
    sideA: CpmAffectedProduct;
    sideB: CpmAffectedProduct;
};

function RadioDot({ chosen }: { chosen: boolean }) {
    return (
        <span
            className="inline-flex items-center justify-center shrink-0"
            style={{
                width: 17,
                height: 17,
                borderRadius: 999,
                border: `1.5px solid ${chosen ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-strong)'}`,
                background: chosen ? 'var(--cpm-brand-purple)' : '#fff',
            }}
        >
            {chosen ? (
                <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="3.4"
                >
                    <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ) : null}
        </span>
    );
}

function volumeKeyOf(v: { volumeMl?: number; volumeUnit?: string }): string {
    return `${v.volumeMl ?? ''}:${v.volumeUnit ?? ''}`;
}

function isLiveProduct(product: CpmAffectedProduct): boolean {
    const key = String(product.normalizedName ?? '').trim();
    return LIVE.has(product.status) && key !== '';
}

function pairKeyOf(product: CpmAffectedProduct): string {
    return String(product.normalizedName ?? '').trim();
}

function variantCountOf(product: CpmAffectedProduct): number {
    if (product.variantCount != null) return product.variantCount;
    return product.variants?.length ?? 0;
}

function statusWord(status: string): string {
    if (status === 'ACTIVE') return 'Already live';
    if (status === 'PENDING') return 'Pending';
    if (status === 'NEEDS_REVIEW') return 'Needs review';
    return status.replaceAll('_', ' ');
}

function syntheticProductDetails(product: CpmAffectedProduct): CpmMasterDetailsResponse {
    return {
        record: {
            _id: product._id,
            name: product.name,
            status: product.status,
        },
        affectedVariants: product.variants ?? [],
    };
}

function pairHasVolumeOverlap(sideA: CpmAffectedProduct, sideB: CpmAffectedProduct): boolean {
    const keysA = new Set((sideA.variants ?? []).map((v) => volumeKeyOf(v)));
    return (sideB.variants ?? []).some((v) => keysA.has(volumeKeyOf(v)));
}

export function findBrandProductCollisionPairs(
    sideA: CpmMasterDetailsResponse | null,
    sideB: CpmMasterDetailsResponse | null
): BrandProductCollisionPair[] {
    if (!sideA || !sideB) return [];
    const liveA = (sideA.affectedProducts ?? []).filter(isLiveProduct);
    const liveB = (sideB.affectedProducts ?? []).filter(isLiveProduct);
    const byKeyA = new Map<string, CpmAffectedProduct>();
    for (const product of liveA) {
        const key = pairKeyOf(product);
        if (!byKeyA.has(key)) byKeyA.set(key, product);
    }
    const pairs: BrandProductCollisionPair[] = [];
    const seen = new Set<string>();
    for (const productB of liveB) {
        const key = pairKeyOf(productB);
        if (seen.has(key)) continue;
        const productA = byKeyA.get(key);
        if (productA == null) continue;
        seen.add(key);
        pairs.push({
            pairKey: key,
            displayName: productA.name || productB.name,
            sideA: productA,
            sideB: productB,
        });
    }
    return pairs;
}

export function brandNonCollidingProductCount(
    sideA: CpmMasterDetailsResponse | null,
    sideB: CpmMasterDetailsResponse | null,
    pairs: BrandProductCollisionPair[]
): number {
    const colliding = new Set(pairs.map((p) => p.pairKey));
    const countSide = (list: CpmAffectedProduct[] | undefined) =>
        (list ?? []).filter((product) => {
            const key = pairKeyOf(product);
            return !(isLiveProduct(product) && colliding.has(key));
        }).length;
    return countSide(sideA?.affectedProducts) + countSide(sideB?.affectedProducts);
}

export function brandMovingProductCount(
    loser: CpmMasterDetailsResponse | null,
    pairs: BrandProductCollisionPair[]
): number {
    const colliding = new Set(pairs.map((p) => p.pairKey));
    return (loser?.affectedProducts ?? []).filter((product) => {
        const key = pairKeyOf(product);
        return !(isLiveProduct(product) && colliding.has(key));
    }).length;
}

export function brandProductCollisionsResolved(
    pairs: BrandProductCollisionPair[],
    productChoices: Record<string, string>,
    volumeChoicesByPair: Record<string, Record<string, string>>,
    volStateChoicesByPair: Record<string, Record<string, Record<string, string>>>
): boolean {
    for (const pair of pairs) {
        if (!productChoices[pair.pairKey]) return false;
        if (
            !productCollisionsResolved(
                syntheticProductDetails(pair.sideA),
                syntheticProductDetails(pair.sideB),
                volumeChoicesByPair[pair.pairKey] ?? {},
                volStateChoicesByPair[pair.pairKey] ?? {}
            )
        ) {
            return false;
        }
    }
    return true;
}

export function buildBrandProductChoicesPayload(
    pairs: BrandProductCollisionPair[],
    productChoices: Record<string, string>,
    volumeChoicesByPair: Record<string, Record<string, string>>,
    volStateChoicesByPair: Record<string, Record<string, Record<string, string>>>
): CpmProductChoice[] {
    return pairs.map((pair) => {
        const volumeChoices = buildProductVolumeChoicesPayload(
            syntheticProductDetails(pair.sideA),
            syntheticProductDetails(pair.sideB),
            volumeChoicesByPair[pair.pairKey] ?? {},
            volStateChoicesByPair[pair.pairKey] ?? {}
        );
        return {
            pairKey: pair.pairKey,
            survivingProductId: productChoices[pair.pairKey],
            ...(volumeChoices.length > 0 ? { volumeChoices } : {}),
        };
    });
}

export type BrandCollisionGaps = {
    unresolvedProducts: string[];
    unresolvedVolumes: number;
    unresolvedStates: number;
};

export function brandCollisionGaps(
    pairs: BrandProductCollisionPair[],
    productChoices: Record<string, string>,
    volumeChoicesByPair: Record<string, Record<string, string>>,
    volStateChoicesByPair: Record<string, Record<string, Record<string, string>>>
): BrandCollisionGaps {
    const unresolvedProducts: string[] = [];
    let unresolvedVolumes = 0;
    let unresolvedStates = 0;
    for (const pair of pairs) {
        if (!productChoices[pair.pairKey]) {
            unresolvedProducts.push(pair.displayName);
            continue;
        }
        const variantsA = pair.sideA.variants ?? [];
        const variantsB = pair.sideB.variants ?? [];
        const collidingKeys = Array.from(
            new Set([...variantsA, ...variantsB].map((v) => volumeKeyOf(v)))
        ).filter(
            (key) =>
                variantsA.some((v) => volumeKeyOf(v) === key) &&
                variantsB.some((v) => volumeKeyOf(v) === key)
        );
        const volMap = volumeChoicesByPair[pair.pairKey] ?? {};
        const stateMap = volStateChoicesByPair[pair.pairKey] ?? {};
        for (const key of collidingKeys) {
            if (!volMap[key]) {
                unresolvedVolumes += 1;
                continue;
            }
            const a = variantsA.find((v) => volumeKeyOf(v) === key);
            const b = variantsB.find((v) => volumeKeyOf(v) === key);
            if (!a || !b) continue;
            const states = Array.from(
                new Set(
                    [...(a.stateProducts ?? []), ...(b.stateProducts ?? [])]
                        .map((sp) => String(sp.state ?? ''))
                        .filter(Boolean)
                )
            );
            const overlap = states.filter(
                (state) =>
                    (a.stateProducts ?? []).some((sp) => String(sp.state ?? '') === state) &&
                    (b.stateProducts ?? []).some((sp) => String(sp.state ?? '') === state)
            );
            const chosen = stateMap[key] ?? {};
            unresolvedStates += overlap.filter((state) => !chosen[state]).length;
        }
    }
    return { unresolvedProducts, unresolvedVolumes, unresolvedStates };
}

export interface BrandProductCollisionsProps {
    sideA: CpmMasterDetailsResponse;
    sideB: CpmMasterDetailsResponse;
    brandNameA: string;
    brandNameB: string;
    pairs: BrandProductCollisionPair[];
    productChoices: Record<string, string>;
    volumeChoicesByPair: Record<string, Record<string, string>>;
    volStateChoicesByPair: Record<string, Record<string, Record<string, string>>>;
    openProdVol: Record<string, boolean>;
    openVolStatesByPair: Record<string, Record<string, boolean>>;
    onPickProduct: (pairKey: string, productId: string) => void;
    onPickVolume: (pairKey: string, volumeKey: string, variantId: string) => void;
    onPickVolState: (pairKey: string, volumeKey: string, state: string, variantId: string) => void;
    onToggleVol: (pairKey: string) => void;
    onToggleVolStates: (pairKey: string, volumeKey: string) => void;
}

export default function BrandProductCollisions({
    sideA,
    sideB,
    brandNameA,
    brandNameB,
    pairs,
    productChoices,
    volumeChoicesByPair,
    volStateChoicesByPair,
    openProdVol,
    openVolStatesByPair,
    onPickProduct,
    onPickVolume,
    onPickVolState,
    onToggleVol,
    onToggleVolStates,
}: BrandProductCollisionsProps) {
    if (pairs.length === 0) return null;

    const gaps = brandCollisionGaps(
        pairs,
        productChoices,
        volumeChoicesByPair,
        volStateChoicesByPair
    );
    const unresolved = gaps.unresolvedProducts.length;
    const soloCount = brandNonCollidingProductCount(sideA, sideB, pairs);
    const subhead =
        unresolved > 0
            ? unresolved === 1
                ? `1 of ${formatCpmCount(pairs.length)} same-name ${pairs.length === 1 ? 'product' : 'products'} still needs a decision — only one product per name can stay live under the merged brand.`
                : `${formatCpmCount(unresolved)} of ${formatCpmCount(pairs.length)} same-name products still need a decision — only one product per name can stay live under the merged brand.`
            : 'All same-name products resolved. Products filed under only one brand move with it.';

    return (
        <div
            className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] flex flex-col"
            style={{
                borderRadius: 16,
                padding: '20px 22px',
                gap: 16,
                boxShadow: '0 1px 2px rgba(14,8,88,.04), 0 12px 32px -8px rgba(14,8,88,.10)',
            }}
        >
            <div className="flex items-baseline flex-wrap" style={{ gap: 12 }}>
                <span
                    className="uppercase text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                >
                    Products with the same name
                </span>
                <span
                    className="text-pretty"
                    style={{
                        fontSize: 13,
                        fontWeight: unresolved > 0 ? 700 : 600,
                        color:
                            unresolved > 0
                                ? 'var(--cpm-warning-text)'
                                : 'var(--cpm-text-secondary)',
                    }}
                >
                    {subhead}
                </span>
            </div>

            {soloCount > 0 ? (
                <div
                    className="flex flex-col border border-[var(--cpm-border-default)] bg-[var(--cpm-surface-subtle)]"
                    style={{ gap: 6, padding: '13px 15px', borderRadius: 12 }}
                >
                    <span
                        className="text-[var(--cpm-text-primary)]"
                        style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '-0.01em' }}
                    >
                        {soloCount === 1
                            ? '1 product is filed under only one brand'
                            : `${formatCpmCount(soloCount)} products are filed under only one brand`}
                    </span>
                    <span
                        className="text-[var(--cpm-text-secondary)] text-pretty"
                        style={{ fontSize: 11.5, fontWeight: 600, lineHeight: 1.45 }}
                    >
                        These are not a choice — they move to the surviving brand as they are. See
                        “Products under this brand” on each card above for the full list.
                    </span>
                </div>
            ) : null}

            <div className="flex flex-col" style={{ gap: 14 }}>
                {pairs.map((pair) => {
                    const chosenId = productChoices[pair.pairKey] || null;
                    const nestedReady = pairHasVolumeOverlap(pair.sideA, pair.sideB);
                    const nestedResolved = productCollisionsResolved(
                        syntheticProductDetails(pair.sideA),
                        syntheticProductDetails(pair.sideB),
                        volumeChoicesByPair[pair.pairKey] ?? {},
                        volStateChoicesByPair[pair.pairKey] ?? {}
                    );
                    const done = !!chosenId && (!nestedReady || nestedResolved);
                    const hasVolumeNested = !!chosenId && nestedReady;
                    const volOpen = openProdVol[pair.pairKey] !== false;
                    const nestedUnres = (() => {
                        if (!chosenId || !nestedReady || nestedResolved) return 0;
                        const g = brandCollisionGaps(
                            [pair],
                            productChoices,
                            volumeChoicesByPair,
                            volStateChoicesByPair
                        );
                        return g.unresolvedVolumes + g.unresolvedStates;
                    })();
                    const options: Array<{
                        product: CpmAffectedProduct;
                        owner: string;
                    }> = [
                        { product: pair.sideA, owner: brandNameA },
                        { product: pair.sideB, owner: brandNameB },
                    ];

                    return (
                        <div
                            key={pair.pairKey}
                            className="flex flex-col"
                            style={{
                                gap: 10,
                                padding: '14px 15px',
                                borderRadius: 12,
                                border: `1px solid ${done ? 'var(--cpm-border-default)' : 'var(--cpm-warning-border)'}`,
                                background: done
                                    ? 'var(--cpm-surface-subtle)'
                                    : 'var(--cpm-warning-bg)',
                            }}
                        >
                            <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                                <span
                                    className="text-[var(--cpm-brand-navy)]"
                                    style={{
                                        fontSize: 14,
                                        fontWeight: 800,
                                        letterSpacing: '-0.015em',
                                    }}
                                >
                                    {pair.displayName}
                                </span>
                                <span
                                    className="inline-flex items-center rounded-full uppercase"
                                    style={{
                                        padding: '3px 9px',
                                        fontSize: 9.5,
                                        fontWeight: 800,
                                        letterSpacing: '0.06em',
                                        background: done
                                            ? 'var(--cpm-success-bg)'
                                            : 'var(--cpm-warning-bg)',
                                        border: `1px solid ${done ? 'var(--cpm-success-border)' : 'var(--cpm-warning-border)'}`,
                                        color: done
                                            ? 'var(--cpm-success-text)'
                                            : 'var(--cpm-warning-text)',
                                    }}
                                >
                                    {!chosenId
                                        ? 'Decision needed'
                                        : done
                                          ? 'Resolved'
                                          : `${formatCpmCount(nestedUnres)} pack ${nestedUnres === 1 ? 'decision' : 'decisions'} left`}
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)] text-pretty"
                                    style={{ fontSize: 12, fontWeight: 600 }}
                                >
                                    {!chosenId
                                        ? 'Both brands carry a live product with this name — pick which one stays; the other is retired into it.'
                                        : done
                                          ? 'This product stays under the merged brand; the other is retired into it.'
                                          : 'Now resolve the packs both products carry at the same volume.'}
                                </span>
                            </div>

                            <div
                                className="grid"
                                style={{
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                    gap: 10,
                                    alignItems: 'start',
                                }}
                            >
                                {options.map((opt) => {
                                    const isChosen = chosenId === opt.product._id;
                                    const role = !chosenId
                                        ? 'none'
                                        : isChosen
                                          ? 'survivor'
                                          : 'retiring';
                                    const other =
                                        opt.product._id === pair.sideA._id
                                            ? pair.sideB
                                            : pair.sideA;
                                    const fields = [
                                        {
                                            key: 'name',
                                            label: 'Product name',
                                            value: opt.product.name,
                                            otherVal: other.name,
                                        },
                                        {
                                            key: 'status',
                                            label: 'Status',
                                            value: statusWord(opt.product.status),
                                            otherVal: statusWord(other.status),
                                        },
                                        {
                                            key: 'variants',
                                            label: 'Variants',
                                            value: `${formatCpmCount(variantCountOf(opt.product))} ${variantCountOf(opt.product) === 1 ? 'pack' : 'packs'}`,
                                            otherVal: `${formatCpmCount(variantCountOf(other))} ${variantCountOf(other) === 1 ? 'pack' : 'packs'}`,
                                        },
                                    ];
                                    return (
                                        <button
                                            key={opt.product._id}
                                            type="button"
                                            onClick={() =>
                                                onPickProduct(pair.pairKey, opt.product._id)
                                            }
                                            className="text-left flex flex-col"
                                            style={{
                                                gap: 9,
                                                padding: '13px 14px',
                                                borderRadius: 12,
                                                cursor: 'pointer',
                                                border: `1.5px solid ${isChosen ? 'var(--cpm-brand-purple)' : 'var(--cpm-border-default)'}`,
                                                background: isChosen
                                                    ? 'rgba(110,100,240,.07)'
                                                    : '#fff',
                                                boxShadow: isChosen
                                                    ? '0 0 0 3px rgba(110,100,240,.20)'
                                                    : undefined,
                                            }}
                                        >
                                            <div className="flex items-center" style={{ gap: 8 }}>
                                                <RadioDot chosen={isChosen} />
                                                <span
                                                    className="min-w-0 truncate text-[var(--cpm-text-primary)]"
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 800,
                                                        letterSpacing: '-0.01em',
                                                    }}
                                                >
                                                    {opt.owner}
                                                </span>
                                                <span
                                                    className="ml-auto shrink-0 inline-flex items-center uppercase whitespace-nowrap"
                                                    style={{
                                                        padding: '2px 8px',
                                                        borderRadius: 999,
                                                        fontSize: 9.5,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.05em',
                                                        background:
                                                            role === 'survivor'
                                                                ? 'var(--cpm-purple-tint-13)'
                                                                : role === 'retiring'
                                                                  ? 'var(--cpm-shortfall-bg)'
                                                                  : 'var(--cpm-surface-skeleton)',
                                                        color:
                                                            role === 'survivor'
                                                                ? 'var(--cpm-brand-purple)'
                                                                : role === 'retiring'
                                                                  ? 'var(--cpm-shortfall-text)'
                                                                  : 'var(--cpm-text-secondary)',
                                                        border:
                                                            role === 'retiring'
                                                                ? '1px solid var(--cpm-shortfall-border)'
                                                                : undefined,
                                                    }}
                                                >
                                                    {role === 'survivor'
                                                        ? 'Kept product'
                                                        : role === 'retiring'
                                                          ? 'Retired into kept'
                                                          : 'On file'}
                                                </span>
                                            </div>
                                            <div className="flex flex-col">
                                                {fields.map((field, index) => {
                                                    const differs = field.value !== field.otherVal;
                                                    return (
                                                        <div
                                                            key={field.key}
                                                            className="flex items-center justify-between"
                                                            style={{
                                                                gap: 10,
                                                                padding: '6px 0',
                                                                borderBottom:
                                                                    index === fields.length - 1
                                                                        ? undefined
                                                                        : '1px solid var(--cpm-border-default)',
                                                            }}
                                                        >
                                                            <span
                                                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                                                style={{
                                                                    fontSize: 10.5,
                                                                    fontWeight: 800,
                                                                    letterSpacing: '0.05em',
                                                                }}
                                                            >
                                                                {field.label}
                                                            </span>
                                                            <span
                                                                style={{
                                                                    fontSize: 12.5,
                                                                    fontWeight: 800,
                                                                    fontVariantNumeric:
                                                                        'tabular-nums',
                                                                    textAlign: 'right',
                                                                    color: differs
                                                                        ? 'var(--cpm-brand-navy)'
                                                                        : 'var(--cpm-text-secondary)',
                                                                    background: differs
                                                                        ? 'rgba(110,100,240,.09)'
                                                                        : undefined,
                                                                    borderRadius: differs
                                                                        ? 6
                                                                        : undefined,
                                                                    padding: differs
                                                                        ? '1px 6px'
                                                                        : undefined,
                                                                }}
                                                            >
                                                                {field.value}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {hasVolumeNested ? (
                                <div
                                    className="flex flex-col overflow-hidden bg-white border border-[var(--cpm-border-default)]"
                                    style={{ borderRadius: 12 }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => onToggleVol(pair.pairKey)}
                                        className="flex items-center w-full"
                                        style={{
                                            gap: 9,
                                            padding: '11px 13px',
                                            background:
                                                nestedUnres === 0
                                                    ? 'var(--cpm-surface-subtle)'
                                                    : 'rgba(212,145,6,.06)',
                                        }}
                                    >
                                        <span
                                            className="inline-flex items-center justify-center shrink-0 bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                                            style={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: 7,
                                            }}
                                        >
                                            <svg
                                                width="13"
                                                height="13"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M5 8h14" />
                                                <path d="M5 12h14" />
                                                <path d="M5 16h9" />
                                            </svg>
                                        </span>
                                        <span
                                            className="uppercase text-[var(--cpm-text-secondary)]"
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 800,
                                                letterSpacing: '0.05em',
                                            }}
                                        >
                                            Same-volume variant collisions
                                        </span>
                                        <span
                                            className="inline-flex items-center rounded-full uppercase"
                                            style={{
                                                padding: '3px 9px',
                                                fontSize: 9.5,
                                                fontWeight: 800,
                                                letterSpacing: '0.06em',
                                                background:
                                                    nestedUnres === 0
                                                        ? 'var(--cpm-success-bg)'
                                                        : 'var(--cpm-warning-bg)',
                                                border: `1px solid ${nestedUnres === 0 ? 'var(--cpm-success-border)' : 'var(--cpm-warning-border)'}`,
                                                color:
                                                    nestedUnres === 0
                                                        ? 'var(--cpm-success-text)'
                                                        : 'var(--cpm-warning-text)',
                                            }}
                                        >
                                            {nestedUnres === 0
                                                ? 'Packs resolved'
                                                : `${formatCpmCount(nestedUnres)} to resolve`}
                                        </span>
                                        <span
                                            className="ml-auto inline-flex shrink-0 text-[var(--cpm-text-tertiary)]"
                                            style={{
                                                transform: volOpen
                                                    ? 'rotate(180deg)'
                                                    : 'rotate(0deg)',
                                                transition: 'transform 150ms ease',
                                            }}
                                        >
                                            <svg
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2.4"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="m6 9 6 6 6-6" />
                                            </svg>
                                        </span>
                                    </button>
                                    {volOpen ? (
                                        <div
                                            className="flex flex-col border-t border-[var(--cpm-border-default)]"
                                            style={{ gap: 12, padding: '13px 14px' }}
                                        >
                                            <ProductVolumeCollisions
                                                sideA={syntheticProductDetails(pair.sideA)}
                                                sideB={syntheticProductDetails(pair.sideB)}
                                                idA={pair.sideA._id}
                                                idB={pair.sideB._id}
                                                survivorId={chosenId}
                                                volumeChoices={
                                                    volumeChoicesByPair[pair.pairKey] ?? {}
                                                }
                                                volStateChoices={
                                                    volStateChoicesByPair[pair.pairKey] ?? {}
                                                }
                                                openVolStates={
                                                    openVolStatesByPair[pair.pairKey] ?? {}
                                                }
                                                onPickVolume={(volumeKey, variantId) =>
                                                    onPickVolume(pair.pairKey, volumeKey, variantId)
                                                }
                                                onPickVolState={(volumeKey, state, variantId) =>
                                                    onPickVolState(
                                                        pair.pairKey,
                                                        volumeKey,
                                                        state,
                                                        variantId
                                                    )
                                                }
                                                onToggleVolStates={(volumeKey) =>
                                                    onToggleVolStates(pair.pairKey, volumeKey)
                                                }
                                                embedded
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
